import React, { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader"
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import Row from "@amzn/meridian/esm/components/row";
import Theme from "@amzn/meridian/esm/components/theme";
import "./Summaries.css";
import { sampleSummaryList } from "../../../localtest/LocalhostTest";
import SummaryCard from "../../common/summary/SummaryCard";
import { fetchPaginatedSummaries, generateSummaryGroup } from "../../utils/SummaryUtil";
import aimsTheme from "../../../meridian-theme-aims.json";

import NoSummary from "./NoSummary";
import Loading from "../../common/Loading";
import CommonErrorModal from "../../common/error/CommonErrorModal";

const SCROLL_THRESHOLD = 300; // pixels from bottom to trigger load more

const Summaries = () => {
  const navigate = useNavigate();
  const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [summaryGroups, setSummaryGroups] = useState(() => {
    const cached = sessionStorage.getItem("summaryCache");
    return cached && cached !== "undefined" ? JSON.parse(cached) : {};
  });
  const [rawSummaries, setRawSummaries] = useState(() => {
    const cached = sessionStorage.getItem("rawSummaries");
    return cached && cached !== "undefined" ? JSON.parse(cached) : [];
  });
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => {
    const cached = sessionStorage.getItem("lastEvaluatedKey");
    return cached && cached !== "undefined" ? JSON.parse(cached) : null;
  });
  const [isEndOfData, setIsEndOfData] = useState(
    sessionStorage.getItem("isEndOfData") === "true"
  );

  // Reference to the scroll container
  const scrollContainerRef = useRef(null);

  // Check if we have data in the cache and set initial state
  useEffect(() => {
    const hasCachedData = Object.keys(summaryGroups).length > 0;
    
    // If we're in localhost and don't have cached data, use sample data
    if (window.location.href === "http://localhost:3000/") {
      console.info("[Localhost] Running in localhost. Using sampleSummaryList.");
      const groups = generateSummaryGroup(sampleSummaryList);
      setSummaryGroups(groups);
    }
    
    // Set initial state based on whether we have data
    setIsSummaryEmpty(!hasCachedData && window.location.href !== "http://localhost:3000/");
    setIsLoading(false);
  }, []);

  // Update isSummaryEmpty when summaryGroups changes
  useEffect(() => {
    const hasSummaries = Object.keys(summaryGroups).length > 0;
    setIsSummaryEmpty(!hasSummaries);
  }, [summaryGroups]);

  const handleScroll = useCallback((evt) => {
    // Don't trigger if already loading or no more data
    if (isLoadingMore || isEndOfData) return;

    const target = evt.target;
    const distanceFromBottom = target.scrollHeight - target.clientHeight - target.scrollTop;

    // Load more when user scrolls within threshold of bottom
    if (distanceFromBottom <= SCROLL_THRESHOLD) {
      setIsLoadingMore(true);
      
      fetchPaginatedSummaries(
        false, 
        lastEvaluatedKey, 
        10, 
        {
          setSummaryGroups,
          setRawSummaries,
          setLastEvaluatedKey,
          setIsEndOfData
        }
      )
        .catch((error) => {
          console.error("Failed to load more summaries:", error);
          setShowServerError(true);
        })
        .finally(() => {
          setIsLoadingMore(false);
        });
    }
  }, [isLoadingMore, isEndOfData, lastEvaluatedKey, rawSummaries]);

  const onViewMoreClick = (summaryData) => {
    navigate(`/meeting/summary/${summaryData.aimsUuid}`, { state: { summary: summaryData } });
  };

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : showServerError ? (
        <CommonErrorModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      ) : isSummaryEmpty ? (
        <NoSummary />
      ) : (
        <Theme tokens={{ boxBorderColorOutline: "#CCD5DE" }}>
          <div className="box-with-background" style={{ width: "370px", height: "90vh" }}>
            <Row spacing="none" spacingInset="none">
              <Row width="32px" />
                <div 
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  style={{ 
                    height: '90vh',
                    overflowY: 'auto',
                    width: '326px'
                  }}
                >
                <Box width="326px" height="auto" spacing="none" spacingInset="none">
                  <Column spacing="none" spacingInset="none">
                    {Object.entries(summaryGroups).map(([groupTitle, summaries]) => (
                      <Box key={groupTitle} width="326px" marginBottom="space100">
                        <Column height="16px" />
                        <Text type="h100">{groupTitle}</Text>
                        <Column height="16px" spacing="none" spacingInset="none" />
                        <Column spacing="none" spacingInset="none">
                          {summaries.map((summary) => (
                            <Column key={summary.aimsUuid} spacing="none" spacingInset="none">
                              <SummaryCard summary={summary} onViewMoreClick={onViewMoreClick} />
                              <Column height="12px" />
                            </Column>
                          ))}
                        </Column>
                      </Box>
                    ))}
                    
                    {/* Loading indicator at bottom of list */}
                    {isLoadingMore && (
                      <Box width="326px">
                        <Column alignmentHorizontal="center">
                          <Theme palette={aimsTheme}>
                            <Loader size="medium" />
                          </Theme>
                        </Column>
                      </Box>
                    )}
                    
                    {/* End of data message */}
                    {isEndOfData && rawSummaries.length > 0 && (
                      <Box width="326px" >
                        <Column alignmentHorizontal="center">
                          <Text type="p100" color="secondary">No more summary</Text>
                        </Column>
                      </Box>
                    )}
                  </Column>
                </Box>
              </div>
            </Row>
          </div>
        </Theme>
      )}
    </>
  );
};

export default Summaries;
